import { useMemo, useState } from "react";
import qrcode from "qrcode-generator";
import type { MfaMethod } from "@baros/core";
import { request } from "./api.js";
import { confirmAuthenticator, startAuthenticator, chooseSecondStep, type AuthenticatorSetup } from "./auth.js";
import { describeAuthError, normaliseCode } from "./password.js";

interface Props {
  readonly token: string;
  /** What Cognito says this person uses now (from `/v1/me`). */
  readonly method: MfaMethod | null;
  /** The organisation requires it of them, so it cannot be turned off. */
  readonly required: boolean;
  /** After any change, once the API has looked again. */
  readonly onChanged: () => void;
}

/** Ask the API to look at Cognito again, so the next request is judged on what is now true. */
const sync = (token: string) => request<{ method: MfaMethod | null }>(token, "POST", "/v1/me/mfa/sync", {});

/**
 * Two-step sign-in (#92): set up an authenticator app, or turn it off. In the account panel, and on the screen somebody
 * whose organisation requires it lands on until it is done.
 *
 * Everything secret happens between the browser and Cognito; the API is
 * only asked to look afterwards.
 */
export function AccountMfa(p: Props) {
  const [setup, setSetup] = useState<AuthenticatorSetup | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  async function run(work: () => Promise<void>, done: string) {
    setBusy(true);
    setMsg(null);
    try {
      await work();
      await sync(p.token);
      setMsg({ ok: true, text: done });
      setSetup(null);
      setCode("");
      p.onChanged();
    } catch (err) {
      setMsg({ ok: false, text: describeAuthError(err, "mfa-setup") });
    } finally {
      setBusy(false);
    }
  }

  const status = p.method === "totp" ? "Authenticator app" : "Off";

  return (
    <div className="account-mfa">
      <div className="label">Two-step sign-in</div>
      <div className="row" style={{ gap: 8 }}>
        <span style={{ fontSize: 13 }}>{status}</span>
        {p.required && <span className="label">Required here</span>}
      </div>

      {!setup && (
        <div className="account-mfa-choices">
          {p.method !== "totp" && (
            <button
              className="btn sm"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                setMsg(null);
                try {
                  setSetup(await startAuthenticator());
                } catch (err) {
                  setMsg({ ok: false, text: describeAuthError(err, "mfa-setup") });
                } finally {
                  setBusy(false);
                }
              }}
            >
              Set up an authenticator app
            </button>
          )}
          {p.method && !p.required && (
            <button
              className="btn sm"
              disabled={busy}
              onClick={() => {
                if (!window.confirm("Turn two-step sign-in off? Your password alone will sign you in.")) return;
                void run(() => chooseSecondStep(null), "Two-step sign-in is off.");
              }}
            >
              Turn off
            </button>
          )}
        </div>
      )}

      {setup && (
        <form
          className="account-pin"
          onSubmit={(e) => {
            e.preventDefault();
            const value = normaliseCode(code);
            if (value) void run(() => confirmAuthenticator(value), "Done. From your next sign-in, Bar OS will ask for the app's code.");
          }}
        >
          <div className="muted" style={{ fontSize: 12, lineHeight: 1.45 }}>
            Scan this with an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password…), then
            type the code it shows.
          </div>
          <QrCode text={setup.uri} />
          <div className="muted" style={{ fontSize: 12, lineHeight: 1.45 }}>
            On this phone? <a href={setup.uri}>Open it in the app</a>, or enter this key by hand:
          </div>
          <code className="mfa-secret">{setup.secret.replace(/(.{4})/g, "$1 ").trim()}</code>
          <input
            autoComplete="one-time-code"
            inputMode="numeric"
            placeholder="Code from the app"
            aria-label="Code from the app"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <div className="row" style={{ gap: 6 }}>
            <button className="btn sm" type="button" disabled={busy} onClick={() => { setSetup(null); setCode(""); }}>
              Cancel
            </button>
            <button className="btn sm" type="submit" disabled={busy || !normaliseCode(code)}>
              Turn on
            </button>
          </div>
        </form>
      )}

      {msg && <div className={`msg ${msg.ok ? "ok" : "err"}`} style={{ fontSize: 12, padding: "8px 10px" }}>{msg.text}</div>}
    </div>
  );
}

/**
 * The secret as a QR code, drawn as SVG squares from the generator's grid
 * rather than injected markup. Always dark on white, whatever the theme:
 * that is what phone cameras read best.
 */
function QrCode({ text }: { readonly text: string }) {
  const cells = useMemo(() => {
    const qr = qrcode(0, "M");
    qr.addData(text);
    qr.make();
    const n = qr.getModuleCount();
    const dark: [number, number][] = [];
    for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (qr.isDark(r, c)) dark.push([r, c]);
    return { n, dark };
  }, [text]);
  const quiet = 4;
  const size = cells.n + quiet * 2;
  return (
    <svg
      className="mfa-qr"
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label="QR code for your authenticator app"
      shapeRendering="crispEdges"
    >
      <rect width={size} height={size} fill="#fff" />
      {cells.dark.map(([r, c]) => (
        <rect key={`${r}-${c}`} x={c + quiet} y={r + quiet} width={1} height={1} fill="#000" />
      ))}
    </svg>
  );
}
